import React, { useState, useRef, useEffect, useCallback } from 'react';
import { formatDistanceToNow } from 'date-fns';
import messageService from '../../services/messageService';

const getInitials = (name) => {
  if (!name) return '??';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  try {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
  } catch {
    return '';
  }
};

// Send a Message Modal Component
const SendMessageModal = ({ isOpen, onClose, onSelectPlayer, userRole }) => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, hasMore: false });
  const modalRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const fetchPlayers = useCallback(async (search = '', page = 1) => {
    setLoading(true);
    try {
      const response = await messageService.getFollowedPlayersForMessaging(userRole, {
        page,
        limit: 20,
        search: search.trim() || undefined
      });

      if (response.data) {
        if (page === 1) {
          setPlayers(response.data.players || []);
        } else {
          setPlayers(prev => [...prev, ...(response.data.players || [])]);
        }
        setPagination(response.data.pagination || { currentPage: 1, totalPages: 1, hasMore: false });
      }
    } catch (error) {
      console.error('Error fetching players:', error);
    } finally {
      setLoading(false);
    }
  }, [userRole]);

  useEffect(() => {
    if (isOpen) {
      fetchPlayers();
      setSearchQuery('');
    }
  }, [isOpen, fetchPlayers]);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (isOpen) {
      searchTimeoutRef.current = setTimeout(() => {
        fetchPlayers(searchQuery, 1);
      }, 300);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, isOpen, fetchPlayers]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleLoadMore = () => {
    if (pagination.hasMore && !loading) {
      fetchPlayers(searchQuery, pagination.currentPage + 1);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div
        ref={modalRef}
        className="bg-jp-card rounded-xl w-full max-w-md max-h-[80vh] flex flex-col shadow-2xl border border-jp-line"
      >
        {/* Modal Header */}
        <div className="p-4 border-b border-jp-line flex items-center justify-between flex-shrink-0">
          <h2 className="text-jp-ink font-semibold text-lg">Send a Message</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-jp-line rounded-lg transition-colors text-jp-muted hover:text-jp-ink"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search Input */}
        <div className="p-4 border-b border-jp-line flex-shrink-0">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-jp-muted" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="11" cy="11" r="8" strokeWidth="2" />
              <path d="m21 21-4.35-4.35" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search followed players..."
              className="w-full pl-10 pr-4 py-2.5 bg-jp-section border border-jp-line rounded-lg text-jp-ink placeholder-jp-muted/70 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/50"
              autoFocus
            />
          </div>
        </div>

        {/* Players List */}
        <div className="flex-1 overflow-y-auto">
          {loading && players.length === 0 ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : players.length > 0 ? (
            <>
              {players.map((player) => {
                const displayName = `${player.firstName || ''} ${player.lastName || ''}`.trim() || 'Unknown';
                const initials = getInitials(displayName);

                return (
                  <div
                    key={player._id}
                    onClick={() => onSelectPlayer(player)}
                    className="flex items-center gap-3 p-4 cursor-pointer transition-colors hover:bg-jp-line border-b border-jp-line"
                  >
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold text-sm overflow-hidden">
                        {player.profileImage ? (
                          <img src={player.profileImage} alt={displayName} className="w-full h-full object-cover" />
                        ) : (
                          initials
                        )}
                      </div>
                    </div>

                    {/* Player Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="text-jp-ink font-medium text-sm truncate">{displayName}</h4>
                      <div className="flex items-center gap-2 mt-0.5">
                        {player.position && (
                          <span className="text-jp-accent-ink text-xs font-medium">{player.position}</span>
                        )}
                        {player.team?.name && (
                          <>
                            <span className="text-jp-muted text-xs">•</span>
                            <span className="text-jp-muted text-xs truncate">{player.team.name}</span>
                          </>
                        )}
                      </div>
                      {player.graduationYear && (
                        <span className="text-jp-muted text-xs">Class of {player.graduationYear}</span>
                      )}
                    </div>

                    {/* Existing Conversation Indicator */}
                    {player.existingConversationId ? (
                      <div className="flex-shrink-0">
                        <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                          Open Chat
                        </span>
                      </div>
                    ) : (
                      <div className="flex-shrink-0">
                        <span className="px-2 py-1 bg-cyan-500/20 text-jp-accent-ink text-xs rounded-full">
                          New Chat
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Load More */}
              {pagination.hasMore && (
                <div className="p-4">
                  <button
                    onClick={handleLoadMore}
                    disabled={loading}
                    className="w-full py-2.5 bg-jp-section hover:bg-jp-line rounded-lg text-jp-ink text-sm font-medium transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Loading...' : 'Load More'}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-40 p-4">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-jp-section rounded-full flex items-center justify-center">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-jp-muted" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-jp-ink font-medium mb-1">No players found</h3>
                <p className="text-jp-muted text-sm">
                  {searchQuery ? 'Try a different search term' : 'Follow players to message them'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const MessagesSidebar = ({
  selectedChat,
  onSelectChat,
  conversations = [],
  loading,
  activeTab,
  onTabChange,
  currentUserId,
  onDeleteConversations,
  onArchiveConversations,
  onUnarchiveConversations,
  onlineUsers = [],
  userRole = 'coach',
  onStartNewConversation,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [messageType, setMessageType] = useState('message');
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [showSendMessageModal, setShowSendMessageModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter conversations by search
  const filteredConversations = conversations.filter((convo) => {
    if (!searchQuery) return true;
    const name = `${convo.participant?.firstName || ''} ${convo.participant?.lastName || ''}`.toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });

  const totalCount = conversations.length;

  const handleMessageTypeChange = (type) => {
    setMessageType(type);
    setShowDropdown(false);
    onSelectChat(null);
    setSelectedMessages([]);
    if (type === 'archive') {
      onTabChange('archive');
    } else if (type === 'requests') {
      onTabChange('requests');
    } else {
      onTabChange('all');
    }
  };

  const getMessageTypeName = () => {
    switch (messageType) {
      case 'message': return 'Message';
      case 'archive': return 'Archive';
      case 'requests': return 'Message requests';
      default: return 'Message';
    }
  };

  const handleCheckboxToggle = (convoId, e) => {
    e.stopPropagation();
    setSelectedMessages(prev =>
      prev.includes(convoId)
        ? prev.filter(id => id !== convoId)
        : [...prev, convoId]
    );
  };

  const handleArchive = () => {
    if (messageType === 'archive') {
      // We're in archive view, so unarchive
      if (onUnarchiveConversations) {
        onUnarchiveConversations(selectedMessages);
      }
    } else {
      if (onArchiveConversations) {
        onArchiveConversations(selectedMessages);
      }
    }
    setSelectedMessages([]);
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (onDeleteConversations) {
      onDeleteConversations(selectedMessages);
    }
    setSelectedMessages([]);
    setShowDeleteModal(false);
  };

  const handleSelectPlayer = async (player) => {
    setShowSendMessageModal(false);

    if (player.existingConversationId) {
      // Open existing conversation
      const existingConvo = conversations.find(c => c._id === player.existingConversationId);
      if (existingConvo) {
        onSelectChat(existingConvo);
      } else {
        // If not in current list, create a mock conversation object to open
        onSelectChat({
          _id: player.existingConversationId,
          participant: {
            _id: player._id,
            firstName: player.firstName,
            lastName: player.lastName,
            profileImage: player.profileImage
          }
        });
      }
    } else {
      // Start new conversation
      if (onStartNewConversation) {
        onStartNewConversation(player);
      }
    }
  };

  return (
    <>
      <div className="w-full md:w-80 lg:w-96 bg-jp-card border-r border-jp-line flex flex-col">
        {/* Selection Action Bar */}
        {selectedMessages.length > 0 && (
          <div className="px-4 py-3 bg-jp-card border-b border-jp-line flex items-center justify-between">
            <span className="text-jp-ink text-sm font-medium">
              {selectedMessages.length} selected
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={handleArchive}
                className="p-2 hover:bg-jp-line rounded-lg transition-colors text-jp-ink"
                title={messageType === 'archive' ? 'Unarchive' : 'Archive'}
              >
                {messageType === 'archive' ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19l3 3m0 0l3-3m-3 3V10M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                )}
              </button>
              <button
                onClick={handleDelete}
                className="p-2 hover:bg-jp-line rounded-lg transition-colors text-jp-ink"
                title="Delete"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="p-3 sm:p-4 border-b border-jp-line flex-shrink-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mb-3 sm:mb-4">
            {/* Message Type Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="w-full flex items-center justify-between gap-1 px-2.5 py-2.5 bg-jp-section hover:bg-jp-line rounded-lg text-jp-ink text-xs font-medium transition-colors overflow-hidden"
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                  <span className="truncate">{getMessageTypeName()}</span>
                </div>
                <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor" className={`transition-transform flex-shrink-0 ${showDropdown ? 'rotate-180' : ''}`}>
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>

              {showDropdown && (
                <div className="absolute top-full left-0 mt-2 w-full sm:w-48 bg-jp-card rounded-lg shadow-xl border border-jp-line overflow-hidden z-50">
                  <button
                    onClick={() => handleMessageTypeChange('message')}
                    className={`w-full flex items-center gap-3 px-4 py-3 ${messageType === 'message' ? 'bg-jp-accent/15' : 'hover:bg-jp-line'} text-jp-ink transition-colors`}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                    </svg>
                    <span className="text-sm font-medium">Message</span>
                  </button>
                  <button
                    onClick={() => handleMessageTypeChange('archive')}
                    className={`w-full flex items-center gap-3 px-4 py-3 ${messageType === 'archive' ? 'bg-jp-accent/15' : 'hover:bg-jp-line'} text-jp-ink transition-colors`}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                    <span className="text-sm font-medium">Archive</span>
                  </button>
                  <button
                    onClick={() => handleMessageTypeChange('requests')}
                    className={`w-full flex items-center gap-3 px-4 py-3 ${messageType === 'requests' ? 'bg-jp-accent/15' : 'hover:bg-jp-line'} text-jp-ink transition-colors`}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                    </svg>
                    <span className="text-sm font-medium whitespace-nowrap">Message requests</span>
                  </button>
                </div>
              )}
            </div>

            {/* Send Message Button - hidden for players */}
            {userRole !== 'player' && (
              <button
                onClick={() => setShowSendMessageModal(true)}
                className="px-2.5 py-2.5 bg-cyan-600 hover:bg-cyan-700 rounded-lg text-white text-xs font-medium transition-colors flex items-center justify-center gap-1.5"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="16" />
                  <line x1="8" y1="12" x2="16" y2="12" />
                </svg>
                <span className="whitespace-nowrap">Send a message</span>
              </button>
            )}
          </div>

          {/* Search */}
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-jp-muted" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="11" cy="11" r="8" strokeWidth="2" />
              <path d="m21 21-4.35-4.35" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Find messages"
              className="w-full pl-10 pr-4 py-2.5 bg-jp-section border border-jp-line rounded-lg text-jp-ink placeholder-jp-muted/70 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/50"
            />
          </div>
        </div>

        {/* Tabs */}
        {messageType === 'message' && (
          <div className="flex gap-1 px-3 sm:px-4 py-2 sm:py-3 border-b border-jp-line flex-shrink-0 overflow-x-auto">
            <button
              onClick={() => onTabChange('all')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeTab === 'all' ? 'bg-jp-line text-jp-ink' : 'text-jp-muted hover:text-jp-ink'}`}
            >
              All {activeTab === 'all' && totalCount > 0 ? `(${totalCount})` : ''}
            </button>
            <button
              onClick={() => onTabChange('coach')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeTab === 'coach' ? 'bg-jp-line text-jp-ink' : 'text-jp-muted hover:text-jp-ink'}`}
            >
              Coach {activeTab === 'coach' && totalCount > 0 ? `(${totalCount})` : ''}
            </button>
            <button
              onClick={() => onTabChange('requests')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeTab === 'requests' ? 'bg-jp-line text-jp-ink' : 'text-jp-muted hover:text-jp-ink'}`}
            >
              Requests {activeTab === 'requests' && totalCount > 0 ? `(${totalCount})` : ''}
            </button>
          </div>
        )}

        {/* Conversations List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredConversations.length > 0 ? (
            filteredConversations.map((convo) => {
              const participant = convo.participant || {};
              const displayName = `${participant.firstName || ''} ${participant.lastName || ''}`.trim() || 'Unknown';
              const initials = getInitials(displayName);
              const hasUnread = (convo.unreadCount || 0) > 0;
              const isOnline = participant._id && onlineUsers.some(id => id.toString() === participant._id.toString());

              return (
                <div
                  key={convo._id}
                  onClick={() => !selectedMessages.length && onSelectChat(convo)}
                  className={`flex items-center gap-2 sm:gap-3 p-3 sm:p-4 cursor-pointer transition-colors border-b border-jp-line ${
                    selectedChat?._id === convo._id
                      ? 'bg-jp-accent/10'
                      : hasUnread
                        ? 'bg-blue-900/10 hover:bg-jp-accent/5'
                        : 'hover:bg-jp-line'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedMessages.includes(convo._id)}
                    onChange={(e) => handleCheckboxToggle(convo._id, e)}
                    className="w-4 h-4 rounded border-jp-line bg-jp-line text-jp-accent-ink focus:ring-2 focus:ring-cyan-400/50 cursor-pointer"
                  />

                  <div className="relative flex-shrink-0">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold text-sm sm:text-base overflow-hidden">
                      {participant.profileImage ? (
                        <img src={participant.profileImage} alt={displayName} className="w-full h-full object-cover" />
                      ) : (
                        initials
                      )}
                    </div>
                    {isOnline && (
                      <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-jp-card rounded-full" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-jp-ink font-medium text-xs sm:text-sm truncate">{displayName}</h4>
                      <span className="text-jp-muted text-xs ml-2 flex-shrink-0">
                        {formatTime(convo.lastMessage?.createdAt || convo.updatedAt)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className={`text-xs sm:text-sm truncate ${hasUnread ? 'text-blue-300 font-medium' : 'text-jp-muted'}`}>
                        {convo.lastMessage
                          ? convo.lastMessage.text
                            ? convo.lastMessage.text
                            : convo.lastMessage.messageType === 'image'
                              ? '📷 Photo'
                              : convo.lastMessage.messageType === 'file'
                                ? `📎 ${convo.lastMessage.file?.name || 'File'}`
                                : 'No messages yet'
                          : 'No messages yet'}
                      </p>
                      {hasUnread && (
                        <span className="ml-2 flex-shrink-0 w-5 h-5 bg-cyan-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                          {convo.unreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="flex items-center justify-center h-full p-8">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-jp-section rounded-full flex items-center justify-center">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-jp-muted" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-jp-ink font-medium mb-1">
                  {messageType === 'archive' ? 'No archived conversations' :
                   activeTab === 'requests' ? 'No message requests' :
                   activeTab === 'coach' ? 'No coach conversations' :
                   'No conversations yet'}
                </h3>
                <p className="text-jp-muted text-sm">
                  {messageType === 'archive' ? 'Archived conversations will appear here' :
                   activeTab === 'requests' ? 'New message requests will appear here' :
                   activeTab === 'coach' ? 'Conversations with coaches will appear here' :
                   'Start a new conversation to get going'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Send Message Modal */}
      <SendMessageModal
        isOpen={showSendMessageModal}
        onClose={() => setShowSendMessageModal(false)}
        onSelectPlayer={handleSelectPlayer}
        userRole={userRole}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-jp-card rounded-xl w-full max-w-sm shadow-2xl border border-jp-line p-6">
            <div className="flex items-center justify-center w-14 h-14 mx-auto mb-4 bg-red-500/20 rounded-full">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-red-400" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <h3 className="text-jp-ink font-semibold text-lg text-center mb-2">Delete Conversation{selectedMessages.length > 1 ? 's' : ''}</h3>
            <p className="text-jp-muted text-sm text-center mb-6">
              This chat will be deleted permanently and cannot be restored. Are you sure you want to delete {selectedMessages.length > 1 ? `these ${selectedMessages.length} conversations` : 'this conversation'}?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2.5 bg-jp-line hover:bg-jp-line rounded-lg text-jp-ink text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm font-medium transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
